/* SCC Apple-grade draft: "Deal a hand" for the product page. No dependencies.
   Every card is painted from the deck's own print art (assets named <prefix>-*.webp, data in <prefix>.js).
   Two kinds of art: ink decks (grey line art tinted onto paper, pips added; Western) and color decks
   (every card as printed, cfg.color, as .webp or, with cfg.ext, .svg; Pastel). Nothing here invents a product fact: names, prices and links come
   from the page; odds are 5-card poker math. */
(function () {
  'use strict';
  if (window.__sccHand) return;
  window.__sccHand = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  var SUITS = ['S', 'H', 'D', 'C'];
  var STRIP = ['S', 'D', 'H', 'C']; /* order of the corner pips in the <prefix>-p.webp strip */
  var RNAME = { A: 'Ace', K: 'King', Q: 'Queen', J: 'Jack', 10: 'Ten', 9: 'Nine', 8: 'Eight', 7: 'Seven', 6: 'Six', 5: 'Five', 4: 'Four', 3: 'Three', 2: 'Two' };
  var RPLURAL = { A: 'Aces', K: 'Kings', Q: 'Queens', J: 'Jacks', 10: 'Tens', 9: 'Nines', 8: 'Eights', 7: 'Sevens', 6: 'Sixes', 5: 'Fives', 4: 'Fours', 3: 'Threes', 2: 'Twos' };
  var SNAME = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };
  /* Exact 5-card poker frequencies out of 2,598,960 deals. */
  var ODDS = { royal: '1 in 649,740', sflush: '1 in 72,193', quads: '1 in 4,165', full: '1 in 694', flush: '1 in 509', straight: '1 in 255', trips: '1 in 47', twopair: '1 in 21', pair: '1 in 2.4', high: '1 in 2' };

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function rankOf(id) { return id.slice(0, -1); }
  function suitOf(id) { return id.slice(-1); }
  function cardName(id) {
    if (id === 'JK' || id === 'JR') return 'Joker';
    return RNAME[rankOf(id)] + ' of ' + SNAME[suitOf(id)];
  }
  function validCard(id) {
    return typeof id === 'string' && RANKS.indexOf(rankOf(id)) > -1 && SUITS.indexOf(suitOf(id)) > -1;
  }
  function fullDeck() {
    var d = [];
    SUITS.forEach(function (s) { RANKS.forEach(function (r) { d.push(r + s); }); });
    return d;
  }
  function randomHand() {
    var d = fullDeck(), out = [], buf = null;
    try { buf = new Uint32Array(5); window.crypto.getRandomValues(buf); } catch (e) { buf = null; }
    for (var i = 0; i < 5; i++) {
      var r = buf ? buf[i] / 4294967296 : Math.random();
      out.push(d.splice(Math.floor(r * d.length), 1)[0]);
    }
    return out;
  }

  /* ---------- Poker hand name ---------- */
  function evaluate(hand) {
    var vals = hand.map(function (c) { return RANKS.indexOf(rankOf(c)); }).sort(function (a, b) { return b - a; });
    var flush = hand.every(function (c) { return suitOf(c) === suitOf(hand[0]); });
    var uniq = vals.filter(function (v, i) { return vals.indexOf(v) === i; });
    var straight = false, top = vals[0];
    if (uniq.length === 5) {
      if (vals[0] - vals[4] === 4) straight = true;
      else if (vals.join() === '12,3,2,1,0') { straight = true; top = 3; } /* A-2-3-4-5 */
    }
    var count = {};
    vals.forEach(function (v) { count[v] = (count[v] || 0) + 1; });
    var groups = Object.keys(count).map(function (v) { return { v: +v, n: count[v] }; })
      .sort(function (a, b) { return b.n - a.n || b.v - a.v; });
    var R = function (v) { return RANKS[v]; };
    if (straight && flush && top === 12) return { key: 'royal', name: 'Royal flush' };
    if (straight && flush) return { key: 'sflush', name: 'Straight flush, ' + RNAME[R(top)] + ' high' };
    if (groups[0].n === 4) return { key: 'quads', name: 'Four ' + RPLURAL[R(groups[0].v)] };
    if (groups[0].n === 3 && groups[1].n === 2) return { key: 'full', name: 'Full house, ' + RPLURAL[R(groups[0].v)] + ' over ' + RPLURAL[R(groups[1].v)] };
    if (flush) return { key: 'flush', name: 'Flush in ' + SNAME[suitOf(hand[0])] };
    if (straight) return { key: 'straight', name: 'Straight, ' + RNAME[R(top)] + ' high' };
    if (groups[0].n === 3) return { key: 'trips', name: 'Three ' + RPLURAL[R(groups[0].v)] };
    if (groups[0].n === 2 && groups[1].n === 2) return { key: 'twopair', name: 'Two pair, ' + RPLURAL[R(groups[0].v)] + ' and ' + RPLURAL[R(groups[1].v)] };
    if (groups[0].n === 2) return { key: 'pair', name: 'Pair of ' + RPLURAL[R(groups[0].v)] };
    return { key: 'high', name: RNAME[R(vals[0])] + ' high' };
  }

  /* ---------- Deck art ---------- */
  function Deck(cfg, urlTpl) {
    this.cfg = cfg;
    this.tpl = urlTpl;
    this.cache = {};
  }
  Deck.prototype.img = function (key) {
    var self = this;
    if (!this.cache[key]) {
      this.cache[key] = new Promise(function (resolve, reject) {
        var im = new Image();
        im.decoding = 'async';
        im.onload = function () { resolve(im); };
        im.onerror = function () { delete self.cache[key]; reject(new Error('scc-hand: missing art ' + key)); };
        var src = self.tpl.replace('KEY', key);
        if (self.cfg.ext) src = src.replace(/\.webp(?=\?|$)/, '.' + self.cfg.ext); /* vector decks ship as .svg */
        im.src = src;
      });
    }
    return this.cache[key];
  };
  Deck.prototype.keysFor = function (id, scale) {
    if (this.cfg.color) {
      /* Color decks: one file per card. c = shown size, x = the sharper copy for close-ups (when the deck has one). */
      var t = scale > 1 && this.cfg.hi ? 'x' : 'c';
      if (id === 'BACK') return [t + 'back'];
      if (id === 'JK') return [t + 'jk'];
      if (id === 'JR') return [t + 'jr'];
      return [t + id.toLowerCase()]; /* file names are lower case: scc-pst-c10h.webp */
    }
    if (id === 'BACK') return ['back'];
    if (id === 'JK' || id === 'JR') return ['jk'];
    var r = rankOf(id);
    /* Shopify asset names are not case sensitive, so the King is stored as 'bk' and the card back as 'back'. */
    if (this.cfg.shared.indexOf(r) > -1) return [r === 'K' ? 'bk' : 'b' + r, 'p'];
    return ['n' + id];
  };
  function rgb(a) { return 'rgb(' + Math.round(a[0]) + ',' + Math.round(a[1]) + ',' + Math.round(a[2]) + ')'; }
  /* Grey art (ink black on white) -> ink on paper, with canvas blend modes only (no pixel reads). */
  function tint(ctx, w, h, paper, ink) {
    var pre = [0, 1, 2].map(function (i) { return clamp(ink[i] / paper[i], 0, 1) * 255; });
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = rgb(pre);
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = rgb(paper);
    ctx.fillRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'source-over';
  }
  /* Paints one card into a canvas. scale 1 = the art's own size; 2 = sharpened for close-ups. */
  Deck.prototype.paint = function (canvas, id, scale) {
    var cfg = this.cfg, self = this;
    scale = scale || 1;
    return Promise.all(this.keysFor(id, scale).map(function (k) { return self.img(k); })).then(function (im) {
      var w = Math.round(cfg.w * scale), h = Math.round(cfg.h * scale);
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(im[0], 0, 0, w, h);
      if (cfg.color) return canvas; /* printed as is: no tint, no sharpening */
      if (id === 'BACK') { tint(ctx, w, h, cfg.backBase, cfg.backStrong); return canvas; }
      var red = id === 'JR' || suitOf(id) === 'H' || suitOf(id) === 'D';
      if (im[1]) {
        var p = cfg.pip[rankOf(id)], n = STRIP.indexOf(suitOf(id));
        var sw = im[1].naturalWidth / 4, sh = im[1].naturalHeight;
        var pw = cfg.pipw * scale, ph = cfg.piph * scale;
        ctx.globalCompositeOperation = 'multiply';
        ctx.drawImage(im[1], n * sw, 0, sw, sh, p[0] * scale - pw / 2, p[1] * scale - ph / 2, pw, ph);
        ctx.save();
        ctx.translate(p[2] * scale, p[3] * scale);
        ctx.rotate(Math.PI);
        ctx.drawImage(im[1], n * sw, 0, sw, sh, -pw / 2, -ph / 2, pw, ph);
        ctx.restore();
      }
      /* Two passes of "overlay" on itself steepen the edges, so enlarged line art stays crisp. */
      ctx.globalCompositeOperation = 'overlay';
      ctx.drawImage(canvas, 0, 0);
      if (scale > 1) ctx.drawImage(canvas, 0, 0);
      tint(ctx, w, h, cfg.paper, red ? cfg.red : cfg.black);
      return canvas;
    });
  };

  /* ---------- Add to cart through the theme's own drawer ---------- */
  function wireAdd(form) {
    if (!form || !window.fetch) return;
    form.addEventListener('submit', function (e) {
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!cart || typeof cart.renderContents !== 'function' || !window.routes) return; /* plain submit goes to the cart */
      e.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-busy');
      var data = new FormData(form);
      try {
        data.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
        data.append('sections_url', window.location.pathname);
        if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
      } catch (err) {}
      fetch(window.routes.cart_add_url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: data })
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (json.status) { window.location.href = window.routes.cart_url; return; }
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(json);
        })
        .catch(function () { window.location.href = (window.routes && window.routes.cart_url) || '/cart'; })
        .then(function () { btn.removeAttribute('aria-disabled'); btn.classList.remove('is-busy'); });
    });
  }

  /* ---------- One section ---------- */
  function init(root) {
    if (root.__scc) return;
    var cfg = (window.SCCDecks || {})[root.getAttribute('data-deck')];
    if (!cfg) return;
    root.__scc = true;
    var deck = new Deck(cfg, root.getAttribute('data-url'));
    var table = root.querySelector('[data-table]');
    var stack = root.querySelector('[data-stack]');
    var holder = root.querySelector('[data-cards]');
    var elName = root.querySelector('[data-rname]');
    var elOdds = root.querySelector('[data-rodds]');
    var elLead = root.querySelector('[data-rlead]');
    var btnDeal = root.querySelector('[data-deal]');
    var btnShare = root.querySelector('[data-share]');
    var btnAll = root.querySelector('[data-all]');
    var ribbon = root.querySelector('[data-ribbon]');
    var zoom = root.querySelector('[data-zoom]');
    if (zoom.parentNode !== document.body) document.body.appendChild(zoom); /* above the sticky header and any transformed parent */
    var deckName = root.getAttribute('data-name') || '';
    var hand = null, busy = false, started = false, cards = [], shared = false;

    wireAdd(root.querySelector('[data-scc-add]'));

    /* The face-down deck: three real backs. */
    for (var s = 0; s < 3; s++) {
      var c = document.createElement('canvas');
      c.className = 'scc-hand__stackcard';
      c.style.setProperty('--n', s);
      c.setAttribute('aria-hidden', 'true');
      stack.appendChild(c);
      deck.paint(c, 'BACK', 1).catch(function () {});
    }

    for (var i = 0; i < 5; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'scc-card';
      b.style.setProperty('--k', i - 2);
      b.style.setProperty('--i', i);
      b.innerHTML = '<span class="scc-card__flip"><canvas class="scc-card__face" width="280" height="392"></canvas><canvas class="scc-card__rear" width="280" height="392"></canvas></span>';
      b.disabled = true;
      holder.appendChild(b);
      cards.push(b);
      deck.paint(b.querySelector('.scc-card__rear'), 'BACK', 1).catch(function () {});
      (function (b, i) {
        b.addEventListener('click', function () { if (!busy && hand) openZoom(hand[i], b); });
      })(b, i);
    }

    function setResult(lead, name, odds) {
      elLead.textContent = lead || '';
      elName.textContent = name || '';
      elOdds.textContent = odds || '';
      root.classList.toggle('has-result', !!name);
    }

    function show(next, lead) {
      var res = evaluate(next);
      hand = next;
      cards.forEach(function (b, i) { b.setAttribute('aria-label', cardName(next[i])); b.disabled = false; });
      setResult(lead, res.name, 'Odds of being dealt this: ' + ODDS[res.key] + '.');
      btnShare.hidden = false;
      btnDeal.textContent = btnDeal.getAttribute('data-again') || 'Deal again';
      busy = false;
      root.classList.remove('is-dealing');
    }

    function deal(next, lead) {
      if (busy) return;
      busy = true;
      started = true;
      root.classList.add('is-dealing', 'has-started');
      setResult('', '', '');
      var paints = next.map(function (id, i) { return deck.paint(cards[i].querySelector('.scc-card__face'), id, 1); });
      var gather = hand && !reduce ? 520 : 0;
      cards.forEach(function (b) { b.classList.remove('is-up', 'is-out'); b.disabled = true; });
      hand = null;
      var wait = new Promise(function (r) { setTimeout(r, gather); });
      Promise.all(paints.concat(wait)).then(function () {
        if (reduce) {
          cards.forEach(function (b) { b.classList.add('is-out', 'is-up'); });
          show(next, lead);
          return;
        }
        root.classList.add('is-riffle');
        setTimeout(function () { root.classList.remove('is-riffle'); }, 520);
        cards.forEach(function (b, i) {
          setTimeout(function () { b.classList.add('is-out'); }, 560 + i * 120);
          setTimeout(function () { b.classList.add('is-up'); }, 560 + i * 120 + 330);
        });
        setTimeout(function () { show(next, lead); }, 560 + 4 * 120 + 330 + 420);
      }).catch(function () {
        /* Art failed to load: leave the deck face down rather than show a broken hand. */
        busy = false;
        root.classList.remove('is-dealing');
        root.classList.add('is-broken');
      });
    }

    btnDeal.addEventListener('click', function () { shared = false; deal(randomHand(), btnDeal.getAttribute('data-lead') || 'You were dealt'); });

    /* A shared link (?hand=AS-KD-10H-7C-2S) opens on that exact hand. */
    var sent = null;
    try {
      var q = new URLSearchParams(window.location.search).get('hand');
      if (q) {
        var ids = q.toUpperCase().split('-');
        var ok = ids.length === 5 && ids.every(validCard) && ids.filter(function (v, i) { return ids.indexOf(v) === i; }).length === 5;
        if (ok) sent = ids;
      }
    } catch (e) {}

    function first() {
      if (started) return;
      if (sent) { shared = true; deal(sent, root.getAttribute('data-sent') || 'The hand you were sent'); }
      else deal(randomHand(), btnDeal.getAttribute('data-lead') || 'You were dealt');
    }
    if (sent) {
      setTimeout(function () {
        try { root.scrollIntoView({ block: 'center' }); } catch (e) { root.scrollIntoView(); }
        first();
      }, 400);
    } else if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) {
        en.forEach(function (e) { if (e.isIntersecting) { io.disconnect(); first(); } });
      }, { threshold: 0.45 });
      io.observe(table);
    } else first();

    /* Share */
    btnShare.addEventListener('click', function () {
      if (!hand) return;
      var res = evaluate(hand);
      var url = window.location.origin + window.location.pathname + '?hand=' + hand.join('-');
      var text = (btnShare.getAttribute('data-text') || '[hand]').replace('[hand]', res.name).replace('[deck]', deckName);
      var label = btnShare.textContent;
      function done(msg) { btnShare.textContent = msg; setTimeout(function () { btnShare.textContent = label; }, 2200); }
      if (navigator.share) { navigator.share({ title: document.title, text: text, url: url }).catch(function () {}); return; }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text + ' ' + url).then(function () { done(btnShare.getAttribute('data-copied') || 'Link copied'); }, function () { window.prompt('', url); });
      } else window.prompt('', url);
    });

    /* Every card, in a ribbon. Painted only when scrolled near. */
    var ribbonBuilt = false;
    function buildRibbon() {
      if (ribbonBuilt) return;
      ribbonBuilt = true;
      var order = [];
      ['S', 'H', 'C', 'D'].forEach(function (su) {
        ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'].forEach(function (r) { order.push(r + su); });
      });
      order.push('JK', 'JR');
      var lazy = 'IntersectionObserver' in window ? new IntersectionObserver(function (en) {
        en.forEach(function (e) {
          if (!e.isIntersecting) return;
          lazy.unobserve(e.target);
          deck.paint(e.target.firstChild, e.target.getAttribute('data-id'), 1).then(function () { e.target.classList.add('is-ready'); }).catch(function () {});
        });
      }, { root: ribbon, rootMargin: '0px 400px' }) : null;
      order.forEach(function (id, n) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'scc-hand__rc';
        b.setAttribute('data-id', id);
        b.setAttribute('aria-label', cardName(id));
        b.style.setProperty('--n', n);
        b.appendChild(document.createElement('canvas'));
        b.addEventListener('click', function () { openZoom(id, b); });
        ribbon.appendChild(b);
        if (lazy) lazy.observe(b);
        else deck.paint(b.firstChild, id, 1).then(function () { b.classList.add('is-ready'); }).catch(function () {});
      });
    }
    if (btnAll) btnAll.addEventListener('click', function () {
      var open = ribbon.hidden;
      ribbon.hidden = !open;
      btnAll.setAttribute('aria-expanded', open ? 'true' : 'false');
      btnAll.textContent = open ? (btnAll.getAttribute('data-close') || 'Hide the cards') : (btnAll.getAttribute('data-open') || 'See every card');
      if (open) { buildRibbon(); root.classList.add('has-ribbon'); }
      else root.classList.remove('has-ribbon');
    });

    /* Close-up: the card grows out of where it sits, then follows the finger. */
    var zCanvas = zoom.querySelector('[data-zcanvas]');
    var zCard = zoom.querySelector('[data-zcard]');
    var zName = zoom.querySelector('[data-zname]');
    var zFrom = null;
    function openZoom(id, from) {
      zFrom = from;
      zName.textContent = cardName(id) + (deckName ? ' · ' + deckName : '');
      deck.paint(zCanvas, id, 2).then(function () {
        zoom.hidden = false;
        document.documentElement.classList.add('scc-hand-zoomed');
        var a = from.getBoundingClientRect();
        var b = zCard.getBoundingClientRect();
        if (!reduce && zCard.animate && b.width) {
          var dx = a.left + a.width / 2 - (b.left + b.width / 2);
          var dy = a.top + a.height / 2 - (b.top + b.height / 2);
          zCard.animate([
            { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + (a.width / b.width) + ')' },
            { transform: 'none' }
          ], { duration: 460, easing: 'cubic-bezier(.2,.8,.2,1)' });
        }
        zoom.classList.add('is-open');
        var x = zoom.querySelector('[data-zclose]');
        if (x) x.focus({ preventScroll: true });
      }).catch(function () {});
    }
    function closeZoom() {
      if (zoom.hidden) return;
      zoom.classList.remove('is-open');
      zoom.hidden = true;
      document.documentElement.classList.remove('scc-hand-zoomed');
      zCard.style.setProperty('--rx', 0);
      zCard.style.setProperty('--ry', 0);
      if (zFrom && zFrom.focus) zFrom.focus({ preventScroll: true });
    }
    zoom.addEventListener('click', function (e) { if (!zCard.contains(e.target) || e.target.closest('[data-zclose]')) closeZoom(); });
    zoom.querySelector('[data-zclose]').addEventListener('click', closeZoom);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeZoom(); });
    if (!reduce) {
      zoom.addEventListener('pointermove', function (e) {
        var r = zCard.getBoundingClientRect();
        var x = clamp((e.clientX - r.left) / r.width, -0.3, 1.3) - 0.5;
        var y = clamp((e.clientY - r.top) / r.height, -0.3, 1.3) - 0.5;
        zCard.style.setProperty('--ry', (x * 22).toFixed(2));
        zCard.style.setProperty('--rx', (-y * 16).toFixed(2));
        zCard.style.setProperty('--gx', (50 + x * 90).toFixed(1));
        zCard.style.setProperty('--gy', (50 + y * 90).toFixed(1));
      }, { passive: true });
      zoom.addEventListener('pointerleave', function () { zCard.style.setProperty('--rx', 0); zCard.style.setProperty('--ry', 0); });
    }
  }

  /* ---------- Sticky buy bar (all product pages) ---------- */
  function initBar(bar) {
    if (bar.__scc) return;
    bar.__scc = true;
    var main = document.querySelector('.product-form__submit');
    var form = document.querySelector('form[data-type="add-to-cart-form"]');
    var btn = bar.querySelector('[data-bar-add]');
    if (!main || !form || !btn || !('IntersectionObserver' in window)) return;
    /* The bar waits for the first buy button on the page (the ring's, when there is one) to scroll away,
       and stands down while any other buy button is on screen. */
    var anchor = document.querySelector('[data-scc-anchor]') || main;
    var twins = [].slice.call(document.querySelectorAll('.scc-hand__form'));
    if (anchor !== main) twins.push(main);
    var past = false, foot = false, seen = [];
    function update() { bar.classList.toggle('is-on', past && !foot && !seen.some(Boolean)); }
    twins.forEach(function (t, n) {
      new IntersectionObserver(function (en) { en.forEach(function (e) { seen[n] = e.isIntersecting; }); update(); }).observe(t);
    });
    new IntersectionObserver(function (en) {
      en.forEach(function (e) { past = !e.isIntersecting && e.boundingClientRect.top < 0; });
      update();
    }).observe(anchor);
    var footer = document.querySelector('footer, .footer');
    if (footer) new IntersectionObserver(function (en) { en.forEach(function (e) { foot = e.isIntersecting; }); update(); }).observe(footer);
    btn.addEventListener('click', function () {
      if (form.requestSubmit) form.requestSubmit(); else main.click();
    });
  }

  function boot() {
    [].forEach.call(document.querySelectorAll('[data-scc-hand]'), init);
    [].forEach.call(document.querySelectorAll('[data-scc-bar]'), initBar);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
  window.SCCHand = { evaluate: evaluate, boot: boot, Deck: Deck, wireAdd: wireAdd, cardName: cardName };
})();
